import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import SalaryDetailForm from '../components/SalaryDetailForm';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';

function SalaryDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [formOpen, setFormOpen] = useState(false);
    const [selectedDetailId, setSelectedDetailId] = useState(null);

    const { data: specification, isLoading: specLoading } = useQuery(
        ['salarySpecification', id],
        () => JobHttpService.loadSalarySpecification(id),
        { enabled: !!id }
    );

    const { data: detailsData, isLoading: detailsLoading, isError, error } = useQuery(
        ['salaryDetails', id],
        () => {
            if (!id) return { results: [], count: 0 };
            return JobHttpService.loadSalaryDetails({ salary_specification: id });
        },
        {
            enabled: !!id,
            keepPreviousData: true
        }
    );

    const deleteDetailMutation = useMutation(
        (detailId) => JobHttpService.deleteSalaryDetail(detailId),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['salaryDetails', id]);
            }
        }
    );

    const details = detailsData?.results || detailsData || [];

    const handleBack = () => {
        navigate(`/employer/salary-specification/${id}`);
    };

    const handleAddClick = () => {
        setSelectedDetailId(null);
        setFormOpen(true);
    };

    const handleEditClick = (detailId) => {
        setSelectedDetailId(detailId);
        setFormOpen(true);
    };

    const handleFormClose = () => {
        setFormOpen(false);
        setSelectedDetailId(null);
    };

    const handleDetailSaved = () => {
        queryClient.invalidateQueries(['salaryDetails', id]);
    };

    const handleDelete = async (detailId) => {
        if (window.confirm('Are you sure you want to delete this salary detail?')) {
            try {
                await deleteDetailMutation.mutateAsync(detailId);
            } catch (err) {
                console.error('Error deleting salary detail:', err);
                alert('Failed to delete salary detail. Please try again.');
            }
        }
    };

    if (specLoading || detailsLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            sx={{ mb: 2 }}
                        >
                            Back
                        </Button>
                        <Typography variant="h4" fontWeight={600} gutterBottom>
                            Salary Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage salary detail items
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                    >
                        Add Salary Detail
                    </Button>
                </Box>

                {isError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error?.message || 'Failed to load salary details'}
                    </Alert>
                )}

                {details.length === 0 && !detailsLoading && (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            No salary details found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Start by adding your first salary detail item
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                        >
                            Add Salary Detail
                        </Button>
                    </Paper>
                )}

                {details.length > 0 && (
                    <Card>
                        <CardContent sx={{ p: 0 }}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Description</TableCell>
                                            <TableCell>Taxable</TableCell>
                                            <TableCell>Recurring</TableCell>
                                            <TableCell>Quantity</TableCell>
                                            <TableCell>Unit Price</TableCell>
                                            <TableCell>Total Amount</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {details.map((detail) => (
                                            <TableRow key={detail.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {detail.description || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={detail.is_taxable ? 'Yes' : 'No'}
                                                        size="small"
                                                        color={detail.is_taxable ? 'primary' : 'default'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={detail.is_recurring ? 'Yes' : 'No'}
                                                        size="small"
                                                        color={detail.is_recurring ? 'primary' : 'default'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {detail.quantity ?? '—'}
                                                </TableCell>
                                                <TableCell>
                                                    {detail.unit_price ? `$${detail.unit_price}` : '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        ${detail.total_amount || '0.00'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditClick(detail.id)}
                                                        color="primary"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(detail.id)}
                                                        color="error"
                                                        disabled={deleteDetailMutation.isLoading}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                )}

                {/* Salary Detail Form Modal */}
                <SalaryDetailForm
                    open={formOpen}
                    onClose={handleFormClose}
                    salaryDetailId={selectedDetailId}
                    salarySpecificationId={id}
                    onSaved={handleDetailSaved}
                />
            </Container>
        </section>
    );
}

export default SalaryDetailsPage;
